import { Activity, House, Plus, User, Users } from 'lucide-react';
import { NavLink, matchPath, useLocation } from 'react-router';
import { cx } from '../lib/cx';
import { useT } from '../lib/i18n';
import { commonMessages } from '../lib/i18n/messages/common';
import { Link } from 'react-router';
import { useAddExpenseTarget } from './addExpenseTarget';
import { useFlowState, useKeyboardInset, useToastOffset } from './hooks';

function Tab({
  to,
  label,
  icon: Icon,
  match,
}: {
  to: string;
  label: string;
  icon: typeof House;
  match?: string[];
}) {
  const location = useLocation();
  const extra = match?.some((p) => matchPath(p, location.pathname)) ?? false;
  return (
    <NavLink
      to={to}
      end={to === '/'}
      className={({ isActive }) =>
        cx(
          'flex min-h-tap flex-col items-center justify-center gap-0.5 text-xs font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-link rounded-control',
          isActive || extra ? 'text-ink' : 'text-ink-muted',
        )
      }
      aria-current={extra ? 'page' : undefined}
    >
      {({ isActive }) => (
        <>
          <span className={cx('rounded-full px-4 py-1', (isActive || extra) && 'bg-accent-soft')}>
            <Icon className="ic" strokeWidth={1.75} aria-hidden />
          </span>
          {label}
        </>
      )}
    </NavLink>
  );
}

/** Mobile bottom tab bar with the raised saffron FAB (DESIGN 3.2, 5.13). */
export function TabBar() {
  const t = useT(commonMessages);
  const keyboard = useKeyboardInset();
  const addTo = useAddExpenseTarget();
  const flowState = useFlowState();
  useToastOffset(80);
  if (keyboard > 0) return null;
  return (
    <nav
      aria-label={t('navMain')}
      className="sticky bottom-0 z-20 grid grid-cols-5 items-center border-t border-line bg-surface/95 px-1 pb-[var(--safe-bottom)] backdrop-blur lg:hidden"
      style={{ minHeight: 'calc(var(--tabbar-h) + var(--safe-bottom))' }}
    >
      <Tab to="/" label={t('navHome')} icon={House} match={['/people/:id']} />
      <Tab to="/groups" label={t('navGroups')} icon={Users} />
      <div className="flex justify-center">
        <Link
          to={addTo}
          state={flowState}
          aria-label={t('addExpense')}
          className="-mt-7 flex h-14 w-14 items-center justify-center rounded-full bg-accent text-accent-fg shadow-fab transition duration-fast ease-standard focus-visible:ring-2 focus-visible:ring-link focus-visible:ring-offset-2 focus-visible:ring-offset-surface focus-visible:outline-none active:scale-[.96]"
        >
          <Plus className="h-7 w-7" strokeWidth={2.25} aria-hidden />
        </Link>
      </div>
      <Tab to="/activity" label={t('navActivity')} icon={Activity} />
      <Tab to="/me" label={t('navMe')} icon={User} />
    </nav>
  );
}
